import React from 'react';
import {Platform, ScrollView, StyleSheet, View} from 'react-native';
import {useTranslation} from 'react-i18next';

import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {EmptyState} from '@/shared/ui/empty-state';
import {AppText} from '@/shared/ui/text';
import {radii, spacing, useTheme} from '@/shared/ui/theme';

/**
 * Что видно, когда рендер бросил исключение.
 *
 * До этого — ничего: одна ошибка в любом компоненте снимала всё дерево, и вместо
 * кабинета оставался белый лист без единого слова. Для приложения, которое ставят на
 * телефон охраны и открывают в браузере бухгалтерии, это худший из возможных отказов:
 * непонятно, сломалось ли приложение, кончился ли интернет или «оно всегда так».
 *
 * Здесь три вещи, и каждая — ответ на вопрос, который человек задаёт в эту секунду:
 *
 *   «Что случилось?»       — прямая фраза, а не извинение.
 *   «Что мне сделать?»     — кнопка, которая ПРАВДА чинит: сброс границы, а на вебе
 *                            перезагрузка страницы, потому что часть отказов живёт в
 *                            уже испорченном состоянии модуля.
 *   «Что сказать в поддержку?» — текст исключения, видимый и выделяемый. Спрятать его
 *                            «чтобы не пугать» значит заставить человека пересказывать
 *                            белый экран.
 *
 * ## Две границы, а не одна
 *
 * Одна стоит вокруг всего приложения (`App.tsx`) — последний рубеж. Вторая — внутри
 * [`Page`](./page.tsx), вокруг СОДЕРЖИМОГО страницы: экран, уронивший свой список,
 * оставляет заголовок, маршрут и панель вкладок на месте, так что уйти с него можно
 * одним касанием, а не перезапуском приложения.
 *
 * Границы обязаны быть классами: `componentDidCatch` не существует в виде хука, и это
 * единственный класс в кодовой базе.
 */
type Props = {
  children: React.ReactNode;
  /**
   * Сброс происходит и при смене этого значения — экран, с которого ушли, не должен
   * оставаться сломанным после возвращения на него.
   */
  resetKey?: string | number;
  /** Компактная подача — для границы внутри страницы, где заголовок уже есть. */
  inline?: boolean;
  testID?: string;
};

type State = {error: Error | null};

const styles = StyleSheet.create({
  root: {flex: 1, justifyContent: 'center', padding: spacing(4), gap: spacing(4)},
  inline: {paddingVertical: spacing(6), gap: spacing(4)},
  /* Пустое состояние тянется во всю высоту по умолчанию — здесь под ним ещё кнопка и
     карточка с текстом ошибки, и растянутое оно отбросило бы их к нижнему краю.
     Именно `flexBasis: 'auto'`, а не `flex: 0`: в React Native короткая запись
     обнуляет и основу, а коробка нулевой высоты — это текст поверх кнопки. */
  state: {flexGrow: 0, flexShrink: 0, flexBasis: 'auto', paddingVertical: 0},
  details: {maxHeight: 160, borderRadius: radii.md, padding: spacing(3)},
  card: {gap: spacing(2), maxWidth: 520, width: '100%', alignSelf: 'center'},
  retry: {alignSelf: 'center', minWidth: 180},
});

/** Тело — функция, чтобы дотянуться до темы и переводов: у класса хуков нет. */
function Fallback({
  error,
  onRetry,
  inline,
}: {
  error: Error;
  onRetry: () => void;
  inline: boolean;
}) {
  const {t} = useTranslation();
  const p = useTheme();

  return (
    <View style={inline ? styles.inline : styles.root} testID="error-boundary-fallback">
      <EmptyState
        tone="error"
        title={t('errors.crashTitle')}
        hint={t('errors.crashHint')}
        style={styles.state}
      />

      <AppButton
        title={t('errors.crashAction')}
        icon="refresh"
        onPress={onRetry}
        style={styles.retry}
        testID="error-boundary-retry"
      />

      <Card padding={3} style={styles.card}>
        <AppText variant="caption">{t('errors.crashDetails')}</AppText>
        <ScrollView style={[styles.details, {backgroundColor: p.muted}]}>
          {/* Выделяемый: сообщение об ошибке нужно скопировать в поддержку, а не
              переписать с экрана от руки. */}
          <AppText variant="mono" selectable testID="error-boundary-message">
            {error.message || String(error)}
          </AppText>
        </ScrollView>
      </Card>
    </View>
  );
}

export class ErrorBoundary extends React.Component<Props, State> {
  state: State = {error: null};

  static getDerivedStateFromError(error: Error): State {
    return {error};
  }

  componentDidUpdate(prev: Props) {
    // Ушли с экрана и вернулись — граница обязана дать ему второй шанс, иначе
    // однажды упавшая страница остаётся сломанной до перезапуска.
    if (prev.resetKey !== this.props.resetKey && this.state.error) {
      this.setState({error: null});
    }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Единственный след, который вообще остаётся: сборщика ошибок в проекте нет, а
    // консоль читают и в браузере, и в `adb logcat`.
    // eslint-disable-next-line no-console
    console.error('[cabinet] render failed', error, info.componentStack);
  }

  retry = () => {
    this.setState({error: null});
    /*
      На вебе — ещё и перезагрузка. Часть отказов живёт не в дереве, а в уже
      испорченном состоянии модуля или в кэше запросов, и «попробовать снова» без
      перезагрузки вернуло бы тот же экран. На нативе перезагрузить нечего, и там
      сброса границы достаточно.
    */
    if (Platform.OS === 'web' && typeof location !== 'undefined') {
      location.reload();
    }
  };

  render() {
    const {error} = this.state;
    if (!error) {
      return this.props.children;
    }
    return (
      <View style={{flex: 1}} testID={this.props.testID}>
        <Fallback error={error} onRetry={this.retry} inline={!!this.props.inline} />
      </View>
    );
  }
}
